import React, { useRef, useState } from 'react';
import {
  Box,
  CardContent,
  Typography,
  IconButton,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useSnackbar } from 'notistack';
import { uploadArticleResource, addArticleResourceUrl, removeArticleResource } from '../../services/api';
import { useTheme } from '@mui/material/styles';

const ResourcePreviewDialog = ({ open, onClose, resource }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!resource) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {resource.source_type === 'url' ? <LinkIcon /> : <InsertDriveFileIcon />}
          {resource.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {resource.source_url && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              component="a" 
              href={resource.source_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'block', 
                mb: 2,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {resource.source_url}
            </Typography>
          )}
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: isDarkMode ? 'grey.900' : 'grey.50',
              color: isDarkMode ? 'grey.100' : 'grey.900',
              borderRadius: 1,
              maxHeight: '60vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              border: 1,
              borderColor: isDarkMode ? 'grey.800' : 'grey.200'
            }}
          >
            {resource.content}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ArticleResources = ({ 
  article, 
  resources = [], 
  onResourcesChange, 
  onProjectResourcesChange 
}) => {
  const [addUrlDialogOpen, setAddUrlDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [urlForm, setUrlForm] = useState({
    source_type: 'url',
    source_url: ''
  });
  const fileInputRef = useRef();
  const { enqueueSnackbar } = useSnackbar();

  // Ensure resources is always an array
  const safeResources = Array.isArray(resources) ? resources : [];

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      await uploadArticleResource(article.id, file);
      onResourcesChange();
      enqueueSnackbar('Resource uploaded successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error uploading resource', { variant: 'error' });
    }
  };

  const handleAddUrl = async () => {
    try {
      await addArticleResourceUrl(article.id, urlForm);
      onResourcesChange();
      setAddUrlDialogOpen(false);
      setUrlForm({ source_type: 'url', source_url: '' });
      enqueueSnackbar('Resource added successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error adding resource', { variant: 'error' });
    }
  };

  const handleRemoveResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to remove this resource?')) return;

    try {
      await removeArticleResource(article.id, resourceId);
      onResourcesChange();
      enqueueSnackbar('Resource removed successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error removing resource', { variant: 'error' });
    }
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
    setPreviewDialogOpen(true);
  };

  return (
    <>
      <CardContent sx={{ 
        py: '8px !important', 
        px: 2,
        '&:last-child': { pb: '8px !important' }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1,
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant="subtitle2">Resources</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.md"
            />
            <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
              <UploadFileIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setAddUrlDialogOpen(true)}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          flexWrap: 'wrap',
          mb: 1,
          minHeight: { xs: 'auto', md: '2.5rem' }
        }}>
          {safeResources.map((resource) => (
            <Chip
              key={resource.id}
              label={resource.name}
              size="small"
              onClick={() => handleResourceClick(resource)}
              onDelete={() => handleRemoveResource(resource.id)}
              icon={resource.source_type === 'url' ? <LinkIcon /> : <InsertDriveFileIcon />}
              deleteIcon={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <PreviewIcon fontSize="small" />
                  <DeleteIcon fontSize="small" />
                </Box>
              }
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            size="small"
            checked={Boolean(article.use_project_resources)}
            onChange={(e) => onProjectResourcesChange(e.target.checked)}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Use project-level resources
            </Typography>
            <Tooltip 
              title="Resources uploaded at the project level will be automatically included in this article. This helps maintain consistency across all articles in the project."
              arrow
              placement="top"
            >
              <InfoIcon 
                sx={{ 
                  fontSize: '1rem', 
                  color: 'text.secondary',
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
        </Box>
      </CardContent>

      {/* Add URL Dialog */}
      <Dialog open={addUrlDialogOpen} onClose={() => setAddUrlDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add URL Resource</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="URL"
              fullWidth
              value={urlForm.source_url}
              onChange={(e) => setUrlForm({ ...urlForm, source_url: e.target.value })}
              required
              placeholder="https://example.com/article"
              helperText="Enter the URL of the resource you want to add"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUrlDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddUrl}
            variant="contained"
            disabled={!urlForm.source_url}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <ResourcePreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setSelectedResource(null);
        }}
        resource={selectedResource}
      />
    </>
  );
};

export default ArticleResources; 